import { getElecHourlyProducts } from "$/api/market-monitoring/electricity.api";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import CustomTooltip from "$/lib/components/graphs/LineGraph/CustomTooltip";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import { getDate } from "$/lib/utils/functions/date.functions";
import { generateYAxisTicks } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { customColors } from "tailwind.config";
import MarketMonitoringDatePicker from "../../MarketMonitoringDatePicker";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";

const LINE_KEYS: { [x: string]: { name: string; color: string } } = {
  "SPOT Horaire": { name: "SPOT Horaire", color: "#000000" },
  "Day-Ahead Base": { name: "Day-Ahead Base", color: "#5978a8" },
  "Day-Ahead Peak": { name: "Day-Ahead Peak", color: "#d45359" },
};

type Props = {
  defaultSelectedDate: Date;
} & CustomDashboardProps;

const HourlyProductsChart = ({
  id,
  defaultSelectedDate,
  isShowcase = false,
  isDraggable = false,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date>(defaultSelectedDate);

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-hourly-products", selectedDate],
    queryFn: () =>
      getElecHourlyProducts({
        date: getDate(selectedDate),
      }),
  });

  const keys = useMemo(() => getArrayKeys(data ?? [], "hour"), [data]);

  const result = getMinMax(data ?? [], keys);

  const toDisplay = useMemo(
    () =>
      data
        ? data.map((item) => {
            const hour = +(
              (item.hour ? item.hour : "0h").split("h")?.[0] ?? "0"
            );

            if (+hour >= 8 && +hour <= 20)
              return {
                ...item,
                peak: [result.min.value - 5, result.max.value + 5],
              };

            return item;
          })
        : [],
    [data, result],
  );

  const lineKeys = useMemo(
    () => keys.filter((key) => key !== "SPOT Horaire"),
    [keys],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const hideKeys = useMemo(
    () =>
      graphVisibility.reduce<string[]>((acc, item) => {
        if (item.isVisible === false) return [...acc, item.name];

        return acc;
      }, []),
    [graphVisibility],
  );

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    setGraphVisibility(
      keys.map((key) => {
        const lineKey = LINE_KEYS[key];
        if (!lineKey) return { name: key, color: "#000000" };
        return {
          name: lineKey.name,
          isVisible: true,
          color: lineKey.color,
        };
      }),
    );
  }, [lineKeys, keys]);

  useEffect(() => {
    setSelectedDate(defaultSelectedDate);
  }, [defaultSelectedDate]);

  return (
    <DisplayBox
      title="Spot Day Ahead Horaire Base et Peak"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      isDraggable={isDraggable}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      id={id}
    >
      <ChartsContainer className="!h-[500px]">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth className="gap-4">
            <ResponsiveContainer width="100%" height={"100%"} minHeight={480}>
              <ComposedChart
                data={toDisplay}
                margin={{ top: 10, right: 30, left: -10, bottom: 0 }}
              >
                <XAxis
                  dataKey="hour"
                  interval="preserveStartEnd"
                  tickCount={10}
                  fontSize={12}
                  tickMargin={5}
                />

                <YAxis
                  tickSize={2}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  tickMargin={5}
                  interval={0}
                  tickFormatter={numberFormatter}
                  ticks={generateYAxisTicks(result.min.value, result.max.value)}
                  domain={[result.min.value, result.max.value]}
                  unit=" €/MWh"
                />

                <Tooltip
                  content={
                    <CustomTooltip
                      unit="€/MWh"
                      hideKeys={["peak", ...hideKeys]}
                    />
                  }
                />

                <Area
                  type="monotone"
                  dataKey="peak"
                  stroke="#e7d9d9"
                  fill="#e7d9d9"
                  fillOpacity={1}
                  tooltipType="none"
                  activeDot={false}
                />

                {graphVisibility.map((item) =>
                  item.name === "SPOT Horaire" ? (
                    <Line
                      key="line-spot-horaire"
                      type="step"
                      dataKey="SPOT Horaire"
                      stroke="black"
                      dot={false}
                      activeDot={false}
                      strokeWidth={
                        item.isVisible === undefined || item.isVisible ? 2 : 0
                      }
                    />
                  ) : (
                    <Line
                      key={"line-" + item.name}
                      type="monotone"
                      dataKey={item.name}
                      stroke={LINE_KEYS[item.name]?.color}
                      display={LINE_KEYS[item.name]?.name}
                      dot={false}
                      activeDot={false}
                      strokeDasharray={"5 5"}
                      strokeWidth={
                        item.isVisible === undefined || item.isVisible ? 2 : 0
                      }
                    />
                  ),
                )}

                <ReferenceLine
                  y={0}
                  stroke={customColors.tertiary.DEFAULT}
                  strokeDasharray={5}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <Flexbox>
            <p className="whitespace-nowrap text-base font-semibold text-secondary">
              Date du spot horaire
            </p>
            <MarketMonitoringDatePicker
              theme="blue"
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
          </Flexbox>

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default HourlyProductsChart;
